<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    @click="handleClick"
  >
    <span v-if="loading" class="btn__spinner">
      <svg class="spinner-icon" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
      </svg>
    </span>
    <span v-if="icon && !loading" class="btn__icon btn__icon--left">
      <i :class="icon"></i>
    </span>
    <span class="btn__content">
      <slot />
    </span>
    <span v-if="iconRight" class="btn__icon btn__icon--right">
      <i :class="iconRight"></i>
    </span>
  </button>
</template>

<script>
export default {
  name: 'BaseButton',
  props: {
    type: {
      type: String,
      default: 'button'
    },
    variant: {
      type: String,
      default: 'filled',
      validator: (v) => ['filled', 'outlined', 'text', 'tonal'].includes(v)
    },
    color: {
      type: String,
      default: 'primary',
      validator: (v) => ['primary', 'success', 'danger', 'warning', 'info', 'default'].includes(v)
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v)
    },
    block: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: null
    },
    iconRight: {
      type: String,
      default: null
    }
  },
  emits: ['click'],
  computed: {
    buttonClasses() {
      return [
        'btn',
        `btn--${this.variant}`,
        `btn--${this.color}`,
        `btn--${this.size}`,
        {
          'btn--block': this.block,
          'btn--rounded': this.rounded,
          'btn--loading': this.loading,
          'btn--disabled': this.disabled
        }
      ]
    }
  },
  methods: {
    handleClick(e) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', e)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-2;
  font-family: $font-family-base;
  font-weight: $font-weight-medium;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all $transition-fast $transition-timing;
  white-space: nowrap;
  outline: none;
  letter-spacing: $letter-spacing-normal;
  user-select: none;

  &:focus-visible {
    box-shadow: 0 0 0 $focus-ring-width $focus-ring-color;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  // Sizes
  &--sm {
    height: $btn-height-sm;
    padding: $btn-padding-sm;
    font-size: $btn-font-size-sm;
    border-radius: $btn-border-radius;
  }

  &--md {
    height: $btn-height-md;
    padding: $btn-padding-md;
    font-size: $btn-font-size-md;
    border-radius: $btn-border-radius;
  }

  &--lg {
    height: $btn-height-lg;
    padding: $btn-padding-lg;
    font-size: $btn-font-size-lg;
    border-radius: $btn-border-radius;
  }

  // Filled variants - Modern flat style with subtle shadows
  &--filled {
    &.btn--primary {
      background-color: $color-primary;
      color: $color-text-inverse;
      &:hover:not(:disabled) {
        background-color: $color-primary-dark;
        box-shadow: $shadow-primary;
      }
    }
    &.btn--success {
      background-color: $color-success;
      color: $color-text-inverse;
      &:hover:not(:disabled) {
        background-color: $color-success-dark;
        box-shadow: $shadow-success;
      }
    }
    &.btn--danger {
      background-color: $color-danger;
      color: $color-text-inverse;
      &:hover:not(:disabled) {
        background-color: $color-danger-dark;
        box-shadow: $shadow-danger;
      }
    }
    &.btn--warning {
      background-color: $color-warning;
      color: $color-text-inverse;
      &:hover:not(:disabled) {
        background-color: $color-warning-dark;
      }
    }
    &.btn--info {
      background-color: $color-info;
      color: $color-text-inverse;
      &:hover:not(:disabled) {
        background-color: $color-info-dark;
      }
    }
    &.btn--default {
      background-color: $color-gray-100;
      color: $color-text-primary;
      &:hover:not(:disabled) {
        background-color: $color-gray-200;
      }
    }
  }

  // Outlined variants - Clean border style
  &--outlined {
    background-color: transparent;
    border: 1px solid;
    &.btn--primary {
      color: $color-primary;
      border-color: $color-primary;
      &:hover:not(:disabled) {
        background-color: $color-primary-50;
      }
    }
    &.btn--success {
      color: $color-success;
      border-color: $color-success;
      &:hover:not(:disabled) {
        background-color: rgba($color-success, 0.08);
      }
    }
    &.btn--danger {
      color: $color-danger;
      border-color: $color-danger;
      &:hover:not(:disabled) {
        background-color: rgba($color-danger, 0.08);
      }
    }
    &.btn--warning {
      color: $color-warning;
      border-color: $color-warning;
      &:hover:not(:disabled) {
        background-color: rgba($color-warning, 0.08);
      }
    }
    &.btn--info {
      color: $color-info;
      border-color: $color-info;
      &:hover:not(:disabled) {
        background-color: rgba($color-info, 0.08);
      }
    }
    &.btn--default {
      color: $color-text-secondary;
      border-color: $color-border-default;
      &:hover:not(:disabled) {
        background-color: $color-gray-50;
        border-color: $color-border-dark;
      }
    }
  }

  // Text variants - Minimal, no background
  &--text {
    background-color: transparent;
    &.btn--primary {
      color: $color-primary;
      &:hover:not(:disabled) {
        background-color: $color-primary-50;
      }
    }
    &.btn--success {
      color: $color-success;
      &:hover:not(:disabled) {
        background-color: rgba($color-success, 0.08);
      }
    }
    &.btn--danger {
      color: $color-danger;
      &:hover:not(:disabled) {
        background-color: rgba($color-danger, 0.08);
      }
    }
    &.btn--warning {
      color: $color-warning;
      &:hover:not(:disabled) {
        background-color: rgba($color-warning, 0.08);
      }
    }
    &.btn--info {
      color: $color-info;
      &:hover:not(:disabled) {
        background-color: rgba($color-info, 0.08);
      }
    }
    &.btn--default {
      color: $color-text-secondary;
      &:hover:not(:disabled) {
        background-color: $color-gray-100;
      }
    }
  }

  // Tonal variants - Soft colored backgrounds
  &--tonal {
    &.btn--primary {
      background-color: $color-primary-50;
      color: $color-primary-dark;
      &:hover:not(:disabled) {
        background-color: $color-primary-100;
      }
    }
    &.btn--success {
      background-color: rgba($color-success, 0.1);
      color: $color-success-dark;
      &:hover:not(:disabled) {
        background-color: rgba($color-success, 0.15);
      }
    }
    &.btn--danger {
      background-color: rgba($color-danger, 0.1);
      color: $color-danger-dark;
      &:hover:not(:disabled) {
        background-color: rgba($color-danger, 0.15);
      }
    }
    &.btn--warning {
      background-color: rgba($color-warning, 0.1);
      color: $color-warning-dark;
      &:hover:not(:disabled) {
        background-color: rgba($color-warning, 0.15);
      }
    }
    &.btn--info {
      background-color: rgba($color-info, 0.1);
      color: $color-info-dark;
      &:hover:not(:disabled) {
        background-color: rgba($color-info, 0.15);
      }
    }
    &.btn--default {
      background-color: $color-gray-100;
      color: $color-text-secondary;
      &:hover:not(:disabled) {
        background-color: $color-gray-200;
      }
    }
  }

  // Modifiers
  &--block {
    width: 100%;
  }

  &--rounded {
    border-radius: $radius-full;
  }

  &--disabled,
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--loading {
    cursor: wait;
  }

  // Icon styles
  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1em;
  }

  // Spinner
  &__spinner {
    display: flex;
    align-items: center;
    justify-content: center;

    .spinner-icon {
      width: 18px;
      height: 18px;
      animation: spin 1s linear infinite;

      circle {
        stroke-dasharray: 60;
        stroke-dashoffset: 45;
      }
    }
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
